<template>
  <v-container fluid class="page-container pa-0" :class="{ preview: preview }">
    <v-app-bar
      :fixed="!preview"
      flat
      dark
      color="transparent"
      class="topbar"
      :class="{
        'topbar--visible': showTopbarColor,
        'elevation-2': showTopbarColor,
      }"
      v-scroll="onScroll"
    >
      <v-btn plain @click="$vuetify.goTo(0, { duration: 1000, offset: 15 })">
        <img
          class="btn-logo-img"
          alt="Brasão de Pernambuco"
          src="/img/brasao_64.png"
        />
      </v-btn>

      <v-app-bar-title class="d-sm-none font-weight-bold">{{ $configurations.app.alias }}</v-app-bar-title>

      <v-spacer></v-spacer>

      <template v-for="(section, index) in content.sections">
        <v-btn
          v-if="section.menuTitle"
          :key="`menu-${index}`"
          :preview="preview"
          class="d-none d-sm-flex"
          plain
          tile
          @click="$vuetify.goTo(`#section-${index}`, { duration: 1000, offset: 15 })"
        >{{ section.menuTitle }}</v-btn>
      </template>

      <v-spacer></v-spacer>
    </v-app-bar>

    <template v-for="(section, index) in content.sections">
      <component
        v-if="sectionComponent(section)"
        :is="sectionComponent(section)"
        :id="`section-${index}`"
        :key="`section-${index}`"
        :preview="preview"
        :section="section"
      ></component>
    </template>

    <lp-social :preview="preview" v-if="hasFooter"></lp-social>
    <lp-footer :preview="preview" v-if="hasFooter"></lp-footer>
  </v-container>
</template>

<script>
import { Cover, default as LpCover } from "components/landing-page/LandingPageCover";
import { Section, Footer, default as LpSection  } from "components/landing-page/LandingPageSection";
import { Faq, FaqItem, default as LpFaq } from "components/landing-page/LandingPageFaq";

import TipoSecaoEnum from "~/commons/enums/tipo-secao.enum";
import LpSocial from "components/landing-page/LandingPageSocial";
import LpFooter from "components/landing-page/LandingPageFooter";

import { LOGOUT, AUTH_NAMESPACE } from "store/modules/auth";
import { createNamespacedHelpers } from "vuex";

const { mapActions: preLoginActions } = createNamespacedHelpers(AUTH_NAMESPACE);

export default {
  name: "LandingPage",
  components: {
    LpCover,
    LpSection,
    LpFaq,
    LpSocial,
    LpFooter,
  },
  props: {
    preview: {
      type: Boolean,
      required: false,
    },
    previewData: {
      type: Object,
      required: false,
    },
  },
  data: () => ({
    content: {
      sections: [],
    },
    showTopbarColor: false,
  }),
  computed: {
    hasFooter() {
      return this.content.sections.find(s => s.type == TipoSecaoEnum.RODAPE);
    },
  },
  mounted() {
    if (!this.preview) {
      this.loadContent().then(() => {
        const faqList = this.content.sections.filter((section) => section.type === TipoSecaoEnum.FAQ);

        if (Array.isArray(faqList) && faqList.length > 0) {
          this.fetchFaqItems().then((items) => {
            // Recria as seções
            const sections = this.content.sections.map((section) => {
              // recria as seções de FAQ com os itens
              if (section.type === TipoSecaoEnum.FAQ) {
                return new Faq({
                  menuTitle: section.menuTitle,
                  title: section.title,
                  items: items,
                }); // novo objeto para provocar reatividade
              }

              return section;
            });

            // Atualiza a lista de seções com as FAQ atualizadas
            this.content.sections = sections;
          });
        }
      });
    } else {
      this.app = this.previewData.app;
      this.content.sections = this.previewData.sections;
      this.showTopbarColor = this.previewData.showTopbarColor;
    }
  },
  methods: {
    ...preLoginActions([
      LOGOUT
    ]),
    sectionComponent(section) {
      const components = {
        capa: "LpCover",
        faq: "LpFaq",
        sobre: "LpSection",
      };

      if (section) {
        return components[section.type];
      }

      return undefined;
    },
    onScroll() {
      const offset = 150;

      if (this.preview) {
        return;
      }

      if (window.pageYOffset > offset) {
        this.showTopbarColor = true;
      } else {
        this.showTopbarColor = false;
      }
    },
    loadContent() {
      const obterImagem = async (section, atributo) => {
        const { data } = await this.$obterImagem({ params: { path: section[atributo] }, responseType: "blob" });
        const getFileName = (filePath) => {
          return filePath.split('\\').pop();
        }
        section[atributo] = new File([data], getFileName(section[atributo]));
      }
      const obterImagensAoCarregar = async (sections) => {
        for (let section of sections) {
          if (section.coverSrc) {
            await obterImagem(section, "coverSrc");
          }
          if (section.logoSrc) {
            await obterImagem(section, "logoSrc");
          }
        }
      }
      return this.$obterConteudoSecoes().then(async ({ data }) => {
        const secoes = JSON.parse(data.json);
        await obterImagensAoCarregar(secoes);
        secoes?.map((section) => {
          switch (section.tipo) {
            case TipoSecaoEnum.CAPA:
              return new Cover(section);
            case TipoSecaoEnum.FAQ:
              return new Faq(section);
            case TipoSecaoEnum.RODAPE:
              return new Footer(section);
            case TipoSecaoEnum.SOBRE:
              return new Section(section);
          }
        }).forEach((section) => {
          this.content.sections.push(section);
        });
      });
    },
    async fetchFaqItems() {
      return this.$obterPerguntasFaq()
        .then(({ data }) => {
          return data.faq.map((item) => {
            return new FaqItem({
              id: item.id,
              title: item.pergunta,
              description: item.resposta,
              tokens: item.palavrasChave,
            });
          });
        })
        .catch(({ response }) => {
          if (response && response.data) {
            this.$notifyWarning(response.data.mensagem);
          } else {
            this.$notifyWarning(
              "Não conseguimos consultar as perguntas e respostas do FAQ."
            );
          }
        });
    },
  },
};
</script>

<style lang="scss" scoped>
$negativeTopbarHeight: -64px;

.topbar {
  transition: all 0.75s;
}

.topbar--visible {
  background-color: #0d47a1 !important;
  border-color: #0d47a1 !important;
}

.page-container {
  .btn-logo-img {
    max-height: 36px;
  }

  &:not(.preview) {
    .cover {
      margin-top: $negativeTopbarHeight;
    }
  }
}
</style>